/* eslint-disable react/jsx-pascal-case */
import { CallMerge } from '@mui/icons-material'
import CancelIcon from '@mui/icons-material/Cancel'
import CheckIcon from '@mui/icons-material/Check'
import PencilIcon from '@mui/icons-material/Create'
import DeleteIcon from '@mui/icons-material/Delete'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import {
  IconButton,
  IconButtonProps,
  SvgIcon,
  SxProps,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { DeepcellDialog, DeepcellDialogProps, SimpleSelectItem } from 'components/shared'
import { Font } from 'plotly.js'
import React, { ReactElement, useState } from 'react'

// Extended categorical color palette to support up to 50 unique colors
//
// Strategy:
// - First 20 colors: Original Vega 20 palette (kept for backwards compatibility)
// - Colors 21-50: Material Design colors - saturated, distinct, research-based palette
//
// After 50 categories, colors will cycle and repeat
//
// See these links for other resources that Ryan suggested
// https://github.com/scverse/scanpy/blob/master/scanpy/plotting/palettes.py
// https://graphicdesign.stackexchange.com/questions/3682/where-can-i-find-a-large-palette-set-of-contrasting-colors-for-coloring-many-d
// http://godsnotwheregodsnot.blogspot.com/2012/09/color-distribution-methodology.html
export const CATEGORICAL_COLOR_PALETTE = [
  '#1f77b4',
  '#ff7f0e',
  '#2ca02c',
  '#d62728',
  '#9467bd',
  '#8c564b',
  '#e377c2',
  '#bcbd22',
  '#17becf',
  '#344a01',
  '#730169',
  '#4f00c2',
  '#b7baff',
  '#1be09a',
  '#907700',
  '#7f0300',
  '#008875',
  '#c50180',
  '#ff9c99',
  '#144674',
  '#7888ff',
  '#d300e2',
  '#48d712',
  '#bb5b00',
  '#8500c0',
  '#ff407f',
  '#693700',
  '#4f4eff',
  '#cd89ff',
  '#cb6d68',
  '#007430',
  '#e9b363',
  '#8f4b7f',
  '#8f0243',
  '#00abfe',
  '#5b5495',
  '#84b870',
  '#40b294',
  '#0004fd',
  '#562e73',
  '#bf8841',
  '#85cafa',
  '#8b36ff',
  '#1093a8',
  '#ff5b40',
  '#685702',
  '#889b00',
  '#f51cba',
  '#b04055',
  '#a13817',
]

// This is the color to show when an attribute is marked as hidden
export const HIDDEN_COLOR = '#ddd'

// The image size and spacing parameters control how the layout algorithm in useZoomThresholdSampleImages.ts works

// Normal image size in pixels on screen
export const CELL_IMAGE_NORMAL_SIZE = 100

// Normal spacing factor
export const CELL_IMAGE_NORMAL_SPACING = 1

// Image sizes in pixels on screen
export const CELL_IMAGE_SIZES: SimpleSelectItem[] = [
  { output: 'Small', value: CELL_IMAGE_NORMAL_SIZE / 1.5 },
  { output: 'Normal', value: CELL_IMAGE_NORMAL_SIZE },
  { output: 'Large', value: CELL_IMAGE_NORMAL_SIZE * 1.5 },
]

// Spacing factor
//
// Layout algorithm will try to keep images at least this far apart:
//     spacing * image size
export const CELL_IMAGE_SPACING_OPTIONS: SimpleSelectItem[] = [
  { output: 'Low', value: CELL_IMAGE_NORMAL_SPACING / 1.5 },
  { output: 'Normal', value: CELL_IMAGE_NORMAL_SPACING },
  { output: 'High', value: CELL_IMAGE_NORMAL_SPACING * 1.5 },
]

export const LabelFont = { size: 14, family: 'Arial Black', color: 'black' } as Font

export const CellVisualizationsSectionHeader = ({
  children,
}: React.PropsWithChildren<unknown>): JSX.Element => {
  return (
    <Typography
      variant="subtitle1"
      component="div"
      sx={{
        lineHeight: '24px',
        fontSize: '16px',
      }}
    >
      {children}
    </Typography>
  )
}

export const CellVisualizationsSubSectionHeader = ({
  children,
}: React.PropsWithChildren<unknown>): JSX.Element => {
  return (
    <Typography
      variant="subtitle1"
      component="div"
      sx={{
        fontWeight: 'bold',
        lineHeight: '20px',
        fontSize: '14px',
      }}
    >
      {children}
    </Typography>
  )
}

export const DefaultIconStyleProps = (color: string): SxProps => ({
  cursor: 'pointer',
  width: '1.2rem',
  color,
})

export interface IconProps {
  onClick?: IconButtonProps['onClick']
  tooltip?: string
  iconButtonProps?: IconButtonProps
  iconStyleProps?: SxProps
  'data-testid'?: string
}

const IconButtonWrapper = ({
  Icon,
  onClick,
  tooltip,
  iconButtonProps,
  iconStyleProps,
  'data-testid': dataTestId,
}: IconProps & { Icon: ReactElement<typeof SvgIcon> }) => {
  const theme = useTheme()
  const {
    grey,
    primary: { main },
  } = theme.palette
  return (
    <Tooltip title={tooltip || ''} enterDelay={1000}>
      <span>
        <IconButton
          {...iconButtonProps}
          onClick={onClick ?? iconButtonProps?.onClick}
          disabled={iconButtonProps?.disabled}
          data-testid={dataTestId}
        >
          <Icon.type
            {...Icon.props}
            sx={
              iconStyleProps ?? DefaultIconStyleProps(iconButtonProps?.disabled ? grey[500] : main)
            }
          />
        </IconButton>
      </span>
    </Tooltip>
  )
}

export const DeleteIconButton = (props: IconProps): JSX.Element => (
  <IconButtonWrapper {...props} Icon={<DeleteIcon />} />
)

export const ChangeIconButton = (props: IconProps): JSX.Element => (
  <IconButtonWrapper {...props} Icon={<PencilIcon />} />
)

export const CloseIconButton = (props: IconProps): JSX.Element => (
  <IconButtonWrapper {...props} Icon={<CancelIcon />} />
)

export const CheckIconButton = (props: IconProps): JSX.Element => (
  <IconButtonWrapper {...props} Icon={<CheckIcon />} />
)

export const MergeIconButton = (props: IconProps): JSX.Element => (
  <IconButtonWrapper {...props} Icon={<CallMerge />} />
)

export const FilterIconButton = (props: IconProps): JSX.Element => (
  <IconButtonWrapper {...props} Icon={<FilterAltOutlinedIcon />} />
)

export type DeleteButtonProps = {
  onConfirmDelete(): void
  dialogProps?: DeepcellDialogProps
  buttonProps?: IconProps
}

export const DeleteButtonWithConfirmation = ({
  onConfirmDelete,
  dialogProps,
  buttonProps,
  children,
}: React.PropsWithChildren<DeleteButtonProps>): JSX.Element => {
  const [open, setOpen] = useState(false)
  return (
    <>
      <DeleteIconButton onClick={() => setOpen(true)} {...buttonProps} />
      <DeepcellDialog
        handleCancel={() => setOpen(false)}
        handleConfirm={onConfirmDelete}
        open={!!open}
        titleLabel="Confirm Delete"
        okLabel="Delete"
        {...dialogProps}
      >
        {children}
      </DeepcellDialog>
    </>
  )
}
